import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Trophy, Eye, Activity, ExternalLink, Tag, RefreshCcw } from "lucide-react";
import { FOLLOWER_TIERS, followerTier, formatNum } from "./followerTiers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Analyse by creator split & top performers" view. Computed entirely from
 * the already-stored report dataset (summary + posts) — every existing
 * report gets this instantly, zero provider calls.
 *
 * The AI "content categories" section is fetched SEPARATELY (cached in
 * Mongo by the backend) so it never slows down the main dashboard.
 */
export const TopPerformers = ({ summary = [], posts = [], reportId }) => {
  const [cats, setCats] = useState(null);
  const [catsLoading, setCatsLoading] = useState(false);

  const loadCats = (refresh = 0) => {
    if (!reportId) return;
    setCatsLoading(true);
    axios.get(`${API}/reports/${reportId}/content-categories${refresh ? "?refresh=1" : ""}`)
      .then(({ data }) => setCats(data))
      .catch(() => setCats(null))
      .finally(() => setCatsLoading(false));
  };
  useEffect(() => { loadCats(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reportId]);

  const tiers = useMemo(() => {
    const foll = {};
    for (const s of summary) foll[s.handle] = Number(s.followers || 0);

    // Best post per handle by views and by ER (per-post ER, same formula as
    // the Overview cards).
    const best = {};
    for (const p of posts) {
      const h = p.handle;
      if (!h) continue;
      const t = p.post_type || "";
      const lk = Number(p.likes || 0), cm = Number(p.comments || 0);
      const sh = p.shares == null ? 0 : Number(p.shares || 0);
      const vw = Number(p.video_views || 0);
      let er = null;
      if (t === "Reel" || t === "Video") {
        if (vw > 0) er = ((lk + cm + sh) / vw) * 100;
      } else if (t === "Static" || t.startsWith("Carousel")) {
        const f = foll[h] || 0;
        if (f > 0) er = ((lk + cm + sh) / f) * 100;
      }
      const b = best[h] || (best[h] = { viewPost: null, viewVal: 0, erPost: null, erVal: -1 });
      if (vw > b.viewVal) { b.viewPost = p; b.viewVal = vw; }
      if (er !== null && er > b.erVal) { b.erPost = p; b.erVal = er; }
    }

    return FOLLOWER_TIERS.map((tier) => {
      const creators = summary.filter((s) => followerTier(s.followers) === tier.id);
      const byViews = creators
        .map((s) => ({ s, v: Number(s.total_video_views || 0) || (best[s.handle]?.viewVal || 0) }))
        .filter((x) => x.v > 0)
        .sort((a, b) => b.v - a.v)
        .slice(0, 3);
      const byEr = creators
        .map((s) => ({ s, er: best[s.handle]?.erVal ?? -1 }))
        .filter((x) => x.er >= 0)
        .sort((a, b) => b.er - a.er)
        .slice(0, 3);
      return { tier, creators, byViews, byEr, best };
    }).filter((t) => t.creators.length > 0);
  }, [summary, posts]);

  if (!summary.length) {
    return (
      <div className="el-card p-10 text-center text-xs font-mono text-slate-400" data-testid="top-performers-empty">
        No creator data in this window yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="top-performers">
      {/* AI content categories — separately fetched + cached, never blocks
          the dashboard. */}
      <div
        className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_12px_-2px_rgba(15,23,42,0.04)]"
        data-testid="content-categories"
      >
        <div className="flex items-center gap-2 mb-3">
          <Tag size={12} className="text-indigo-600" />
          <span className="text-xs font-semibold text-slate-800">Content categories being promoted</span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-indigo-400 bg-indigo-50 border border-indigo-100 rounded-full px-1.5 py-0.5">AI</span>
          <button
            onClick={() => loadCats(1)}
            disabled={catsLoading}
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-indigo-600 disabled:opacity-40 transition-colors"
            title="Recompute (uses AI — takes a few seconds)"
            data-testid="categories-refresh-btn"
          >
            <RefreshCcw size={10} className={catsLoading ? "animate-spin" : ""} />
            {catsLoading ? "analysing…" : "recompute"}
          </button>
        </div>
        {catsLoading && !cats && (
          <div className="space-y-2" data-testid="categories-loading">
            <div className="animate-pulse bg-slate-200/70 rounded-md h-5 w-2/3" />
            <div className="animate-pulse bg-slate-200/70 rounded-md h-5 w-1/2" />
          </div>
        )}
        {!catsLoading && (!cats || !cats.available) && (
          <div className="text-[11px] font-mono text-slate-400" data-testid="categories-empty">
            No categorizable captions in this report yet.
          </div>
        )}
        {cats?.available && (
          <div className="flex flex-wrap gap-2" data-testid="categories-overall">
            {cats.overall.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50/60 px-2.5 py-1 text-[11px] text-indigo-700"
                title={c.example || ""}
                data-testid={`category-chip-${i}`}
              >
                <span className="font-semibold">{c.category}</span>
                <span className="font-mono text-indigo-400">{c.share}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {tiers.map(({ tier, creators, byViews, byEr, best }) => (
        <div
          key={tier.id}
          className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_12px_-2px_rgba(15,23,42,0.04)]"
          data-testid={`tier-section-${tier.id}`}
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Trophy size={12} className="text-amber-500" />
            <span className="text-xs font-semibold text-slate-800">{tier.label}</span>
            <span className="text-[10px] font-mono text-slate-400">
              {tier.hint} · {creators.length} creator{creators.length === 1 ? "" : "s"}
            </span>
            {cats?.tiers?.[tier.id]?.length > 0 && (
              <span className="flex flex-wrap gap-1.5 ml-1" data-testid={`tier-categories-${tier.id}`}>
                {cats.tiers[tier.id].map((c, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600"
                    title={c.example || ""}
                  >
                    {c.category} <span className="font-mono text-slate-400">{c.share}</span>
                  </span>
                ))}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top by views */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                <Eye size={10} className="text-cyan-600" /> Top by views
              </div>
              {byViews.length === 0 && (
                <div className="text-[11px] font-mono text-slate-300 py-2">no video views in this tier</div>
              )}
              <div className="space-y-1.5">
                {byViews.map(({ s, v }, i) => {
                  const b = best[s.handle];
                  return (
                    <div
                      key={s.handle}
                      className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2"
                      data-testid={`topviews-row-${tier.id}-${i}`}
                    >
                      <span className="text-[10px] font-mono text-slate-400 w-4">#{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-800 truncate">
                          @{s.handle}
                          {s.owner_full_name && s.owner_full_name !== s.handle && (
                            <span className="ml-1.5 text-[11px] text-slate-400 font-normal">{s.owner_full_name}</span>
                          )}
                        </div>
                        {b?.viewPost && (
                          <a
                            href={b.viewPost.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] font-mono text-indigo-500 hover:text-indigo-600"
                          >
                            {b.viewPost.post_type} · {formatNum(b.viewVal)} views <ExternalLink size={9} />
                          </a>
                        )}
                      </div>
                      <span className="text-xs font-mono font-semibold text-cyan-700 tabular-nums">{formatNum(v)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Top by ER */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                <Activity size={10} className="text-pink-600" /> Top by engagement rate
              </div>
              {byEr.length === 0 && (
                <div className="text-[11px] font-mono text-slate-300 py-2">no rated content in this tier</div>
              )}
              <div className="space-y-1.5">
                {byEr.map(({ s, er }, i) => {
                  const b = best[s.handle];
                  return (
                    <div
                      key={s.handle}
                      className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2"
                      data-testid={`toper-row-${tier.id}-${i}`}
                    >
                      <span className="text-[10px] font-mono text-slate-400 w-4">#{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-800 truncate">
                          @{s.handle}
                          {s.owner_full_name && s.owner_full_name !== s.handle && (
                            <span className="ml-1.5 text-[11px] text-slate-400 font-normal">{s.owner_full_name}</span>
                          )}
                        </div>
                        {b?.erPost && (
                          <a
                            href={b.erPost.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] font-mono text-indigo-500 hover:text-indigo-600"
                          >
                            {b.erPost.post_type} · {b.erVal.toFixed(2)}% ER <ExternalLink size={9} />
                          </a>
                        )}
                      </div>
                      <span className="text-xs font-mono font-semibold text-pink-700 tabular-nums">{er.toFixed(2)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
